import { useEffect, useRef, useState } from 'react';
import { IonSpinner } from '@ionic/react';
import { TriangleAlert } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Modal } from '../ui/Modal';
import { Icons } from '../../services/iconService';
import { notify } from '../../services/notify';
import { useStartDropoffRun } from '../../hooks/useOrders';
import type { ListingOrderSummary } from '../../services/orderService';
import type { TimeWindow } from '../../types/listing';
import { formatTimeWindow, presentWindow } from '../../utils/timeWindow';
import { zonedWallClockToInstant } from '../../utils/timezone';

interface DropoffRunPanelProps {
  listingId: string;
  /** All of the listing's orders, terminal ones included (the run's progress
   *  counts completed deliveries); drop-off ones are picked out here. */
  orders: ListingOrderSummary[];
  /** When the run started (listing.dropoffRunStartedAt). Once set the panel
   *  switches from the start control to the run's progress. */
  runStartedAt?: string | null;
  /** Deep-linked here (`?run=1`, from a notification-center order card):
   *  scroll the panel into view and flash a highlight ring, same treatment a
   *  deep-linked order card gets. */
  highlight?: boolean;
  /** The listing's drop-off window. Once it ends with no run started there's
   *  nothing left to start: the backend refuses it and refunds the orders. */
  dropoffWindow?: TimeWindow | null;
}

/**
 * The chef's "I'm leaving the house" control for a listing's drop-off orders.
 *
 * Drop-off is one run per listing, not a per-order "ready" tap: starting it
 * sends every accepted drop-off order out for delivery and tells each eater the
 * food is on the way. It stays locked while any drop-off order is still waiting
 * on an accept or decline, since an order left undecided when the chef drives
 * off has no way onto the run. The backend enforces the same rule.
 *
 * Renders nothing when there's nothing for a run to do.
 */
export const DropoffRunPanel: React.FC<DropoffRunPanelProps> = ({
  listingId,
  orders,
  runStartedAt,
  highlight,
  dropoffWindow,
}) => {
  const { t } = useTranslation(['listing', 'common']);
  const startRun = useStartDropoffRun();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const [flashHighlight, setFlashHighlight] = useState(false);
  // Mirrors OrderCard's deep-link treatment. rAF defers the scroll one frame so
  // it runs after layout settles on first paint.
  useEffect(() => {
    if (!highlight) return;
    const raf = requestAnimationFrame(() => {
      rootRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
    setFlashHighlight(true);
    const timer = setTimeout(() => setFlashHighlight(false), 2500);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(timer);
    };
  }, [highlight]);

  const dropoffOrders = orders.filter((o) => o.fulfillmentMethod === 'dropoff');
  const pendingCount = dropoffOrders.filter(
    (o) => o.status === 'paid' || o.status === 'received',
  ).length;
  const readyCount = dropoffOrders.filter((o) => o.status === 'accepted').length;

  // Run under way (or finished): progress instead of the start control. Drop-off
  // is closed to new orders from here, so nothing can be waiting to start.
  // "Out" counts every order the run took: still on the way, delivered, or a
  // door nobody answered.
  const onTheWay = dropoffOrders.filter((o) => o.status === 'ready_to_pickup').length;
  const delivered = dropoffOrders.filter((o) => o.status === 'completed').length;
  const wentOut = onTheWay + delivered + dropoffOrders.filter((o) => o.status === 'no_show').length;

  const title = !runStartedAt
    ? t('detail.dropoffRun.title')
    : onTheWay > 0
      ? t('detail.dropoffRun.inProgressTitle')
      : t('detail.dropoffRun.doneTitle');

  const header = (
    <div className="flex items-center gap-2">
      <span
        className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full"
        style={{ backgroundColor: 'var(--ion-color-tertiary)', color: '#000' }}
      >
        <Icons.dropoff className="h-4 w-4" aria-hidden />
      </span>
      <p className="text-sm font-semibold text-foreground">{title}</p>
    </div>
  );
  const panelClass = `space-y-2 rounded-xl border bg-card p-3 transition-shadow duration-500 ${
    flashHighlight ? 'border-brand-primary ring-2 ring-brand-primary' : 'border-border'
  }`;

  if (runStartedAt) {
    if (wentOut === 0) return null;
    return (
      <div ref={rootRef} className={panelClass}>
        {header}
        <p className="text-sm text-muted-foreground">
          {onTheWay > 0
            ? t('detail.dropoffRun.inProgressBody', { delivered, total: wentOut })
            : delivered === wentOut
              ? t('detail.dropoffRun.doneBody', { count: delivered })
              : t('detail.dropoffRun.doneBodyPartial', { delivered, total: wentOut })}
        </p>
      </div>
    );
  }

  if (readyCount === 0 && pendingCount === 0) return null;

  const dropWin = presentWindow(dropoffWindow);
  const windowEnded =
    !!dropWin && zonedWallClockToInstant(dropWin.end, dropWin.timezone).getTime() < Date.now();
  // Leaving before the window opens is allowed, but the eaters were promised
  // the window, so the confirm says that they'll hear "on the way" early.
  const startsLater =
    !!dropWin && zonedWallClockToInstant(dropWin.start!, dropWin.timezone).getTime() > Date.now();
  if (windowEnded) {
    return (
      <div ref={rootRef} className={panelClass}>
        {header}
        <p className="text-sm text-muted-foreground">{t('detail.dropoffRun.windowEnded')}</p>
      </div>
    );
  }

  const blocked = pendingCount > 0 || readyCount === 0;

  const handleStart = () => {
    startRun.mutate(
      { listingId },
      {
        onSuccess: (res) => {
          setConfirmOpen(false);
          notify.success(t('detail.dropoffRun.started', { count: res.ordersOnTheirWay }));
        },
        // Errors surface through the global error modal (DROPOFF_RUN_PENDING_ORDERS
        // is translated in errors.json); just close the confirm behind it.
        onError: () => setConfirmOpen(false),
      },
    );
  };

  return (
    <div ref={rootRef} className={panelClass}>
      {header}
      <p className="text-sm text-muted-foreground">
        {pendingCount > 0
          ? t('detail.dropoffRun.pending', { count: pendingCount })
          : t('detail.dropoffRun.ready', { count: readyCount })}
      </p>
      <button
        type="button"
        className="w-full rounded-xl bg-brand-primary py-3 text-base font-semibold text-white active:opacity-80 disabled:opacity-50"
        disabled={blocked || startRun.isPending}
        onClick={() => setConfirmOpen(true)}
      >
        {t('detail.dropoffRun.cta')}
      </button>

      <Modal
        isOpen={confirmOpen}
        onClose={() => !startRun.isPending && setConfirmOpen(false)}
        size="sm"
        className="space-y-4"
      >
        <h3 className="text-lg font-bold">{t('detail.dropoffRun.confirmTitle')}</h3>
        <p className="text-sm text-muted-foreground">
          {t('detail.dropoffRun.confirmBody', { count: readyCount })}
        </p>
        {startsLater && dropWin ? (
          <div
            className="flex items-start gap-2 rounded-lg border px-3 py-2 text-sm text-foreground"
            style={{ borderColor: 'var(--ion-color-warning)' }}
          >
            <TriangleAlert
              className="mt-0.5 h-4 w-4 shrink-0"
              style={{ color: 'var(--ion-color-warning-shade)' }}
              aria-hidden
            />
            <p>{t('detail.dropoffRun.earlyWarning', { window: formatTimeWindow(dropWin) })}</p>
          </div>
        ) : null}
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setConfirmOpen(false)}
            disabled={startRun.isPending}
            className="flex-1 rounded-lg btn-outline border-border py-2.5 text-sm font-semibold text-foreground active:opacity-80 disabled:opacity-50"
          >
            {t('common:actions.cancel')}
          </button>
          <button
            type="button"
            onClick={handleStart}
            disabled={startRun.isPending}
            className="flex-1 rounded-lg bg-brand-primary py-2.5 text-sm font-semibold text-white active:opacity-80 disabled:opacity-50"
          >
            {startRun.isPending ? (
              <IonSpinner name="crescent" className="h-4 w-4" />
            ) : (
              t('detail.dropoffRun.confirmCta')
            )}
          </button>
        </div>
      </Modal>
    </div>
  );
};
